import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Banknote,
  Building2,
  Wallet,
  ArrowUpFromLine,
  Copy,
  Check,
  AlertCircle,
  TrendingUp,
  Smartphone,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Badge } from "@/shared/components/ui/badge";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { THAI_BANKS } from "@/shared/constants/thaiBanks";

const USDT_NETWORKS = [
  { code: "TRC20", name: "Tron (TRC20)", fee: "1 USDT", time: "~1 min" },
  { code: "ERC20", name: "Ethereum (ERC20)", fee: "~15 USDT", time: "~5 min" },
  { code: "BEP20", name: "BNB Chain (BEP20)", fee: "~0.5 USDT", time: "~1 min" },
];

// Mock exchange rate - in production this would come from an API
const EXCHANGE_RATES = {
  THB: 35.25,
  PKR: 278.50,
};

export default function WithdrawalForm({
  onSubmit,
  submitting = false,
  stats = { total: 0, pendingAmount: 0, pendingCount: 0 },
  accentColor = "emerald", // emerald, sky, violet, blue
  currency
}) {
  const normalizedCurrency = String(currency || "THB").toUpperCase();
  let t = null;
  try {
    const langContext = useLanguage();
    t = langContext?.t;
  } catch (e) {
    // Not wrapped in LanguageProvider
  }
  const translate = (key) => {
    if (key === "amountThb") return `${t ? t("amount") : "Amount"} (${normalizedCurrency})`;
    if (key === "thbEquivalent") return `(${normalizedCurrency}) ${t ? t("equivalent") : "Equivalent"}`;

    if (t) return t(key);
    const fallbacks = {
      bankTransfer: "Bank Transfer",
      usdtCrypto: "USDT Crypto",
      selectBankLabel: "Select Bank",
      chooseYourBank: "Choose your bank",
      accountNumber: "Account Number",
      enterAccountNumber: "Enter your bank account number",
      accountHolderName: "Account Holder Name",
      enterAccountHolderName: "Enter account holder name",
      amountThb: t ? `${t("amount")} (${normalizedCurrency})` : `Amount (${normalizedCurrency})`,
      minimumWithdrawal: "Minimum withdrawal",
      withdrawToBank: "Withdraw to Bank",
      networkLabel: "Network",
      walletAddress: "Wallet Address",
      enterWalletAddress: "Enter your {network} USDT address",
      amountUsdt: "Amount (USDT)",
      thbEquivalent: `${normalizedCurrency} Equivalent`,
      exchangeRate: "Exchange Rate",
      withdrawUsdt: "Withdraw USDT",
      withdrawalSummary: "Withdrawal Summary",
      totalWithdrawn: "Total Withdrawal",
      pending: "Pending",
      requests: "requests",
      processingTime: "Processing Time",
      hours: "hours",
      mins: "mins",
      needHelp: "Need Help?",
      contactSupport: "Contact support if your withdrawal takes longer than expected.",
      walletWarning: "Please ensure the wallet address is correct and supports",
      incorrectAddressWarning: "Incorrect addresses may result in permanent loss of funds.",
      processing: "Processing...",
      fee: "Fee",
    };
    return fallbacks[key] || key;
  };

  const currencySymbol = normalizedCurrency === "PKR" ? "Rs" : "฿";
  const [activeTab, setActiveTab] = useState("bank");
  const [errors, setErrors] = useState({});

  // Bank form state
  const [bankForm, setBankForm] = useState({
    bank_code: "",
    account_number: "",
    account_name: "",
    amount: "",
    bank_name: "",
  });

  // USDT form state
  const [usdtForm, setUsdtForm] = useState({
    network: "TRC20",
    wallet_address: "",
    amount: "",
  });

  const [copied, setCopied] = useState(false);

  const setFieldError = (field, message) => {
    setErrors((prev) => ({ ...prev, [field]: message }));
  };

  const clearFieldError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateBankForm = () => {
    const nextErrors = {};
    const amountValue = Number(bankForm.amount);

    if (!bankForm.bank_code) nextErrors.bank_code = "Please select a bank";
    if (!bankForm.account_number?.trim()) nextErrors.account_number = "Account number is required";
    if (!bankForm.account_name?.trim()) nextErrors.account_name = "Account holder name is required";
    if (!bankForm.amount || Number.isNaN(amountValue) || amountValue < 100) {
      nextErrors.amount = `Minimum withdrawal is ${currencySymbol}100`;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateUsdtForm = () => {
    const nextErrors = {};
    const amountValue = Number(usdtForm.amount);

    if (!usdtForm.network) nextErrors.network = "Please select a network";
    if (!usdtForm.wallet_address?.trim()) nextErrors.wallet_address = "Wallet address is required";
    if (!usdtForm.amount || Number.isNaN(amountValue) || amountValue < 10) {
      nextErrors.amount = "Minimum withdrawal is 10 USDT";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // Calculate PKR equivalent for USDT
  // const thbEquivalent = useMemo(() => {
  //   const usdtAmount = parseFloat(usdtForm.amount) || 0;
  //   return usdtAmount * USDT_TO_PKR_RATE;
  // }, [usdtForm.amount]);

  // Calculate equivalent for USDT
  const currentRate = EXCHANGE_RATES[normalizedCurrency] || EXCHANGE_RATES.THB;
  const thbEquivalent = useMemo(() => {
    const usdtAmount = parseFloat(usdtForm.amount) || 0;
    return usdtAmount * currentRate;
  }, [usdtForm.amount, currentRate]);

  const handleBankSubmit = (e) => {
    e.preventDefault();
    if (!validateBankForm()) return;
    onSubmit({
      type: "bank",
      ...bankForm,
      amount: Number(bankForm.amount),
    });
    setBankForm({ bank_code: "", account_number: "", account_name: "", amount: "", bank_name: "" });
    setErrors({});
  };

  const handleUsdtSubmit = (e) => {
    e.preventDefault();
    if (!validateUsdtForm()) return;
    onSubmit({
      type: "usdt",
      ...usdtForm,
      amount: Number(usdtForm.amount),
      thb_equivalent: thbEquivalent,
    });
    setUsdtForm({ network: "TRC20", wallet_address: "", amount: "" });
    setErrors({});
  };

  const FieldError = ({ field }) =>
    errors[field] ? <p className="text-xs text-red-500 mt-1">{errors[field]}</p> : null;

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const colorClasses = {
    emerald: {
      bg: "bg-emerald-600 hover:bg-emerald-700",
      light: "bg-emerald-100 text-emerald-700",
      border: "border-emerald-200",
      text: "text-emerald-600",
      gradient: "from-emerald-500 to-teal-600",
    },
    sky: {
      bg: "bg-sky-600 hover:bg-sky-700",
      light: "bg-sky-100 text-sky-700",
      border: "border-sky-200",
      text: "text-sky-600",
      gradient: "from-sky-500 to-cyan-600",
    },
    violet: {
      bg: "bg-violet-600 hover:bg-violet-700",
      light: "bg-violet-100 text-violet-700",
      border: "border-violet-200",
      text: "text-violet-600",
      gradient: "from-violet-500 to-purple-600",
    },
    blue: {
      bg: "bg-blue-600 hover:bg-blue-700",
      light: "bg-blue-100 text-blue-700",
      border: "border-blue-200",
      text: "text-blue-600",
      gradient: "from-blue-500 to-indigo-600",
    },
  };

  const colors = colorClasses[accentColor] || colorClasses.emerald;

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Main Form */}
      <div className="lg:col-span-2">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-slate-100 overflow-hidden"
        >
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="border-b border-slate-100 px-6 pt-6">
              <TabsList className="grid w-full grid-cols-2 bg-slate-100 p-1">
                <TabsTrigger
                  value="bank"
                  className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                >
                  <Building2 className="h-4 w-4" />
                  {translate("bankTransfer")}
                </TabsTrigger>
                <TabsTrigger
                  value="usdt"
                  className="gap-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                >
                  <Wallet className="h-4 w-4" />
                  {translate("usdtCrypto")}
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Bank Transfer Tab */}
            <TabsContent value="bank" className="p-6 mt-0">
              <form onSubmit={handleBankSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{translate("selectBankLabel")}</Label>
                  <Select
                    value={bankForm.bank_code}
                    onValueChange={(v) => {
                      const bank = THAI_BANKS.find((b) => b.code === v);
                      setBankForm({
                        ...bankForm,
                        bank_code: v,
                        bank_name: bank ? bank.name : "",
                      });
                      clearFieldError("bank_code");
                    }}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder={translate("chooseYourBank")} />
                    </SelectTrigger>
                    <SelectContent side="bottom" avoidCollisions={false} className="max-h-56">
                      {THAI_BANKS.map((bank) => (
                        <SelectItem key={bank.code} value={bank.code}>
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${bank.color || "bg-slate-400"}`} />
                            <span>{bank.name}</span>
                            <span className="text-slate-400">({bank.shortName || bank.code})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError field="bank_code" />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">{translate("accountNumber")}</Label>
                  <Input
                    value={bankForm.account_number}
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/\D/g, "");
                      setBankForm({ ...bankForm, account_number: digitsOnly });
                      clearFieldError("account_number");
                    }}
                    onBlur={(e) => {
                      if (!e.target.value.trim()) {
                        setFieldError("account_number", "Account number is required");
                      }
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder={translate("enterAccountNumber")}
                    className="h-12 font-mono"
                  />
                  <FieldError field="account_number" />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">{translate("accountHolderName")}</Label>
                  <Input
                    value={bankForm.account_name}
                    onChange={(e) => {
                      const lettersOnly = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                      setBankForm({ ...bankForm, account_name: lettersOnly });
                      clearFieldError("account_name");
                    }}
                    onBlur={(e) => {
                      if (!e.target.value.trim()) setFieldError("account_name", "Account holder name is required");
                    }}
                    inputMode="text"
                    placeholder={translate("enterAccountHolderName")}
                    className="h-12"
                  />
                  <FieldError field="account_name" />
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">{translate("amountThb")}</Label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">Rs</span>
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">{currencySymbol}</span>
                    <Input
                      type="text"
                      value={bankForm.amount}
                      onChange={(e) => {
                        const numericValue = e.target.value.replace(/[^0-9.]/g, "");
                        const normalizedValue = numericValue.replace(/(\..*)\./g, "$1");
                        setBankForm({ ...bankForm, amount: normalizedValue });
                        clearFieldError("amount");
                      }}
                      onBlur={(e) => {
                        const amountValue = Number(e.target.value);
                        if (!e.target.value || Number.isNaN(amountValue) || amountValue < 100) {
                          setFieldError("amount", "Minimum withdrawal is Rs100");
                        }
                      }}
                      placeholder="0.00"
                      className="h-14 pl-8 text-xl font-semibold"
                      min="100"
                      inputMode="decimal"
                      pattern="[0-9]*[.]?[0-9]*"
                    />
                  </div >
                  <p className="text-xs text-slate-500">{translate("minimumWithdrawal")}: {currencySymbol}100</p>
                </div >

                <Button
                  type="submit"
                  disabled={submitting || !bankForm.bank_code || !bankForm.account_number || !bankForm.amount}
                  className={`w-full h-12 ${colors.bg} gap-2 text-base font-medium`}
                >
                  {submitting ? (
                    <>
                      <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {translate("processing")}
                    </>
                  ) : (
                    <>
                      <ArrowUpFromLine className="h-5 w-5" />
                      {translate("withdrawToBank")}
                    </>
                  )}
                </Button>
              </form >
            </TabsContent >

            {/* USDT Tab */}
            < TabsContent value="usdt" className="p-6 mt-0" >
              <form onSubmit={handleUsdtSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{translate("networkLabel")}</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {USDT_NETWORKS.map((network) => (
                      <button
                        key={network.code}
                        type="button"
                        onClick={() => {
                          setUsdtForm({ ...usdtForm, network: network.code });
                          clearFieldError("network");
                        }}
                        className={`p-3 rounded-xl border-2 text-left transition-all outline-none focus:outline-none ${usdtForm.network === network.code
                          ? `${colors.border} ${colors.light}`
                          : "border-slate-200 hover:border-slate-300"
                          }`}
                      >
                        <p className="font-semibold text-sm">{network.code}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{translate("fee")}: {network.fee}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">{translate("walletAddress")}</Label>
                  <div className="relative">
                    <Input
                      value={usdtForm.wallet_address}
                      onChange={(e) => {
                        setUsdtForm({ ...usdtForm, wallet_address: e.target.value });
                        clearFieldError("wallet_address");
                      }}
                      onBlur={(e) => {
                        if (!e.target.value.trim()) setFieldError("wallet_address", "Wallet address is required");
                      }}
                      placeholder={translate("enterWalletAddress").replace("{network}", usdtForm.network)}
                      className="h-12 font-mono text-sm pr-10"
                    />
                    {usdtForm.wallet_address && (
                      <button
                        type="button"
                        onClick={() => copyAddress(usdtForm.wallet_address)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">{translate("amountUsdt")}</Label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
                    <Input
                      type="text"
                      value={usdtForm.amount}
                      onChange={(e) => {
                        const numericValue = e.target.value.replace(/[^0-9.]/g, "");
                        const normalizedValue = numericValue.replace(/(\..*)\./g, "$1");
                        setUsdtForm({ ...usdtForm, amount: normalizedValue });
                        clearFieldError("amount");
                      }}
                      onBlur={(e) => {
                        const amountValue = Number(e.target.value);
                        if (!e.target.value || Number.isNaN(amountValue) || amountValue < 10) {
                          setFieldError("amount", "Minimum withdrawal is 10 USDT");
                        }
                      }}
                      placeholder="0.00"
                      className="h-14 pl-8 text-xl font-semibold"
                      min="10"
                      step="0.01"
                      inputMode="decimal"
                      pattern="[0-9]*[.]?[0-9]*"
                    />
                  </div>
                  <FieldError field="amount" />
                </div>

                {/* PKR Conversion Display */}
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{
                    opacity: usdtForm.amount ? 1 : 0,
                    height: usdtForm.amount ? "auto" : 0
                  }}
                  className="overflow-hidden"
                >
                  <div className={`p-4 rounded-xl bg-gradient-to-r ${colors.gradient} text-white`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm opacity-80">{translate("thbEquivalent")}</p>
                        <p className="text-2xl font-bold">{currencySymbol}{thbEquivalent.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </div >
                      <div className="text-right">
                        <p className="text-xs opacity-80">{translate("exchangeRate")}</p>
                        <div className="flex items-center gap-1">
                          <TrendingUp className="h-3 w-3" />
                          <span className="text-sm font-medium">1 USDT = {currencySymbol}{currentRate}</span>
                        </div >
                      </div >
                    </div >
                  </div >
                </motion.div >

                <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700">
                    {translate("walletWarning")} {usdtForm.network}. {translate("incorrectAddressWarning")}
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={submitting || !usdtForm.wallet_address || !usdtForm.amount}
                  className={`w-full h-12 ${colors.bg} gap-2 text-base font-medium`}
                >
                  {submitting ? (
                    <>
                      <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {translate("processing")}
                    </>
                  ) : (
                    <>
                      <Wallet className="h-5 w-5" />
                      {translate("withdrawUsdt")}
                    </>
                  )}
                </Button>
              </form >
            </TabsContent >
          </Tabs >
        </motion.div >
      </div >

      {/* Summary Sidebar */}
      < div className="space-y-4" >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-6 border border-slate-100"
        >
          <h3 className="font-semibold text-slate-900 mb-4">{translate("withdrawalSummary")}</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-slate-500">{translate("totalWithdrawn")}</span>
              <span className={`font-semibold ${colors.text}`}>
                {currencySymbol}{stats.total.toLocaleString()}
              </span >
            </div >
            <div className="flex justify-between items-center">
              <span className="text-slate-500">{translate("pending")}</span>
              <div className="text-right">
                <span className="font-semibold text-amber-600">
                  {currencySymbol}{stats.pendingAmount.toLocaleString()}
                </span >
                <p className="text-xs text-slate-400">{stats.pendingCount} {translate("requests")}</p>
              </div >
            </div >
          </div >
        </motion.div >

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-slate-50 rounded-xl p-4 border border-slate-200"
        >
          <h4 className="font-medium text-slate-900 mb-2">{translate("processingTime")}</h4>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-slate-400" />
                <span className="text-slate-600">{translate("bankTransfer")}</span>
              </div>
              <span className="text-slate-500">1-24 {translate("hours")}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wallet className="h-4 w-4 text-slate-400" />
                <span className="text-slate-600">USDT</span>
              </div>
              <span className="text-slate-500">10-60 {translate("mins")}</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-blue-50 border border-blue-200 rounded-xl p-4"
        >
          <h4 className="font-medium text-blue-900 mb-2">{translate("needHelp")}</h4>
          <p className="text-sm text-blue-700">
            {translate("contactSupport")}
          </p>
        </motion.div>
      </div >
    </div >
  );
}